import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  AgreementLevelAnswer,
  AgreementLevelQuestion,
  CareerReadinessAnswer,
} from '../../assessments/career-readiness/career-readiness.types';
import character from '../../assets/images/intake/step26/char.svg';
import { useCareerReadinessContext } from '../../hooks/use-form-context.hooks';
import { usePreEmploymentAssessmentContext } from '../../hooks/use-pre-employment-assessment.hooks';
import {
  CareerReadinessState,
  dispatchUpdateCareerReadiness,
} from '../../redux/slice/form-career-readiness';
import { AgreementLevel } from '../../redux/slice/form-employment-readiness';
import {
  PreEmploymentAssessmentStatus,
  dispatchUpsertPreEmploymentAssessment,
} from '../../redux/slice/pre-employment-assessment';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface ComponentProps {
  questions: AgreementLevelQuestion[];
  onBack: () => void;
  step: number;
}

type AnswersState = {
  [questionId: string]: CareerReadinessAnswer;
};

const radio_options = [
  {
    label: 'Strongly Disagree',
    value: AgreementLevel.STRONGLY_DISAGREE,
  },
  {
    label: 'Disagree',
    value: AgreementLevel.DISAGREE,
  },
  {
    label: 'Somewhat',
    value: AgreementLevel.NEUTRAL,
  },
  {
    label: 'Agree',
    value: AgreementLevel.AGREE,
  },
  {
    label: 'Strongly Agree',
    value: AgreementLevel.STRONGLY_AGREE,
  },
];

function convertValueAgreementLevelToString(
  answer: CareerReadinessAnswer,
): 'strongly_disagree' | 'disagree' | 'neutral' | 'agree' | 'strongly_agree' | undefined {
  const typedAnswer: AgreementLevelAnswer = answer as AgreementLevelAnswer;
  switch (typedAnswer?.answer) {
    case undefined:
      return undefined;
    case AgreementLevel.STRONGLY_DISAGREE:
      return 'strongly_disagree';
    case AgreementLevel.DISAGREE:
      return 'disagree';
    case AgreementLevel.NEUTRAL:
      return 'neutral';
    case AgreementLevel.AGREE:
      return 'agree';
    case AgreementLevel.STRONGLY_AGREE:
      return 'strongly_agree';
  }
}

export const CareerReadinessAssessment: React.FC<ComponentProps> = ({
  onBack,
  questions,
  step,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const savedAnswers: CareerReadinessState = useCareerReadinessContext();
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState<AnswersState>({});

  function updateAnswer(questionId: string, updatedAnswer: Partial<CareerReadinessAnswer>): void {
    setError('');
    setAnswers((previousState: AnswersState) => ({
      ...previousState,
      [questionId]: {
        ...answers[questionId],
        ...updatedAnswer,
      },
    }));
  }

  const hasMissingAnswers: boolean = questions.some((question: AgreementLevelQuestion): boolean => {
    if (!answers[question.id]) {
      return true;
    }
    return false;
  });

  const { handleSubmit } = useForm();
  const backButtonClick = () => {
    onBack();
  };
  const {
    requiresCareerReadinessAssessment,
    progress,
    defaultStepProgress,
    status: preEmploymentAssessmentStatus,
  } = usePreEmploymentAssessmentContext();

  useEffect(() => {
    if (preEmploymentAssessmentStatus === PreEmploymentAssessmentStatus.COMPLETE) {
      setIsDisabled(true);
    }
  }, [preEmploymentAssessmentStatus]);
  const submitAssessment = async () => {
    logger.debug('Submitted career readiness form.', answers);
    const error_message: string = 'Please select an option for each question';
    setIsLoading(true);
    try {
      if (hasMissingAnswers) {
        setError(error_message);
      } else {
        if (isDisabled) {
          navigate('/pre-employment/experience/step1');
        } else {
          await dispatch(dispatchUpdateCareerReadiness(answers));
          if (!requiresCareerReadinessAssessment) {
            navigate('/pre-employment/experience/step1');
            if (defaultStepProgress) {
              const totalProgress = (progress ?? 0) + defaultStepProgress;
              void dispatch(
                dispatchUpsertPreEmploymentAssessment({
                  progress: savedAnswers[questions[0].id] ? progress : totalProgress,
                  activeStepUrl: `/pre-employment/experience/step1`,
                }),
              );
            } else {
              void dispatch(
                dispatchUpsertPreEmploymentAssessment({
                  activeStepUrl: `/pre-employment/experience/step1`,
                }),
              );
            }
          } else {
            if (defaultStepProgress) {
              const totalProgress = (progress ?? 0) + defaultStepProgress;
              await dispatch(
                dispatchUpsertPreEmploymentAssessment({
                  progress: Object.keys(savedAnswers).length ? progress : totalProgress,
                  activeStepUrl: `/pre-employment/career-readiness/step${step + 1}`,
                }),
              );
            } else {
              await dispatch(
                dispatchUpsertPreEmploymentAssessment({
                  activeStepUrl: `/pre-employment/career-readiness/step${step + 1}`,
                }),
              );
            }
            navigate(`/pre-employment/career-readiness/step${step + 1}`);
          }
        }
      }
    } catch {
      toast.error('Something went wrong!');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (savedAnswers && Object.keys(savedAnswers).length) {
      setAnswers(savedAnswers);
    }
  }, [savedAnswers]);

  return (
    <div>
      <p className="text-lg mt-7 md:px-10">
        Career readiness is the awareness and development of the skills one needs to confidently and
        successfully meet employer expectations and transition into work in your career choice.
      </p>
      <div className="flex  mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">27</div>
        <div>
          <h4 className="text-2xl">
            To what extent
            <span className="font-semibold"> do you agree or disagree </span>
            with the following statements?
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-211px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className=" md:px-16"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAssessment)}
      >
        <ol className="pl-5" style={{ listStyle: 'upper-latin' }} type="A">
          {questions.map((question: AgreementLevelQuestion) => (
            <li className="mb-5 font-semibold" key={question.id}>
              {question.question}
              <div className="rating-btns flex items-center flex-wrap mt-3">
                {radio_options.map((option) => (
                  <div key={option.label} className="h-[55px] mr-4 mb-3 lg:mb-0">
                    <label className="radio-container h-full block relative">
                      <input
                        disabled={isDisabled}
                        checked={
                          convertValueAgreementLevelToString(answers[question.id]) === option.value
                        }
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          updateAnswer(question.id, {
                            question: question.question,
                            answer: event.target.value as AgreementLevel,
                          })
                        }
                        value={option.value}
                        className="w-full h-full absolute top-0 left-0 opacity-0"
                        type="radio"
                        name={`interview-${question.id}`}
                      />
                      <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                        {option.label}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ol>
        {error && <p className="text-red-500 font-light !text-sm pt-5">{error}</p>}
        <div className="flex justify-end lg:justify-between lg:mt-20 mt-10">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-internal"
          >
            Back
          </button>
          <button className="btn-internal ml-5" disabled={isLoading}>
            Next
          </button>
        </div>
      </form>
    </div>
  );
};
